import React, { createContext, useContext, useEffect } from 'react';

import { useAuth } from 'hooks';
import { getRequest } from 'services';

import { configureThumbnailToken, ThumbnailToken } from './signed-thumbnail.store';
import { useThumbnailToken } from './useThumbnailToken';

const ThumbnailTokenContext = createContext<ThumbnailToken | null>(null);

/**
 * Mount ONCE near the app root. Owns the single subscription to the token
 * store, so every <Thumbnail> just reads the current value from context
 * instead of each running its own fetch-on-mount check.
 *
 * The token endpoint requires an authenticated session, so the fetcher is
 * only wired up (and only ever called) once the user is authenticated —
 * either right after login, or on a page refresh where a session already
 * exists and no token has been fetched yet.
 */
export function SignedThumbnailProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;

    configureThumbnailToken(() => getRequest('images/thumbnail-token').then((res) => res.data as ThumbnailToken));
  }, [isAuthenticated]);

  const token = useThumbnailToken(isAuthenticated);

  return (
    <ThumbnailTokenContext.Provider value={token}>
      {children}
    </ThumbnailTokenContext.Provider>
  );
}

export function useSignedThumbnailToken(): ThumbnailToken | null {
  return useContext(ThumbnailTokenContext);
}
